'use client';

import { UploadCloud, Link as LinkIcon, PackageCheck, Play, ArrowRight, Sparkles } from 'lucide-react';

interface HowItWorksSectionProps {
  onVideoClick?: () => void;
}

export function HowItWorksSection({ onVideoClick }: HowItWorksSectionProps) {
  const hasDemoVideo = Boolean(process.env.NEXT_PUBLIC_DEMO_VIDEO_URL);

  return (
    <section className="py-24 bg-background relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-[20%] left-[10%] w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[20%] right-[10%] w-72 h-72 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted/50 border border-border/50 text-xs font-medium text-muted-foreground mb-6">
            <span className="flex h-2 w-2 rounded-full bg-primary animate-pulse" />
            Streamlined Workflow
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">
            Your System of Record.
            <br />
            <span className="text-muted-foreground font-medium">Built for evidence.</span>
          </h2>
          <p className="text-xl text-muted-foreground/80 leading-relaxed max-w-2xl mx-auto">
            From messy spreadsheets to auditor-ready packs in three logical steps. No magic, just smart engineering.
          </p>
        </div>

        {/* Process Grid */}
        <div className="relative max-w-6xl mx-auto mb-20">
          {/* Connector Line (Desktop) */}
          <div className="hidden md:block absolute top-[3.5rem] left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-transparent via-border to-transparent -z-10" />

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            
            {/* Step 1: IMPORT */}
            <div className="relative group">
              <div className="w-28 h-28 mx-auto bg-background rounded-2xl border border-border shadow-sm flex items-center justify-center mb-8 group-hover:border-primary/50 group-hover:shadow-md transition-all duration-300 relative z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-muted/50 to-transparent rounded-2xl -z-10" />
                <UploadCloud className="w-10 h-10 text-primary" />
                <div className="absolute -top-3 -right-3 w-8 h-8 bg-background border border-border rounded-full flex items-center justify-center text-sm font-bold text-muted-foreground shadow-sm">1</div>
              </div>
              <div className="text-center px-4">
                <h3 className="text-xl font-semibold mb-3 text-foreground">Import Inventory</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Upload your master asset list via CSV or Excel. Our smart mapper handles your custom columns seamlessly.
                </p>
                <div className="inline-flex items-center gap-1.5 text-xs font-medium text-primary/80 bg-primary/5 px-2.5 py-1 rounded-md">
                  <ArrowRight className="w-3 h-3" />
                  <span>Smart Field Mapping</span>
                </div>
              </div>
            </div>

            {/* Step 2: LINK */}
            <div className="relative group">
              <div className="w-28 h-28 mx-auto bg-background rounded-2xl border border-border shadow-sm flex items-center justify-center mb-8 group-hover:border-blue-500/50 group-hover:shadow-md transition-all duration-300 relative z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-muted/50 to-transparent rounded-2xl -z-10" />
                <LinkIcon className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                <div className="absolute -top-3 -right-3 w-8 h-8 bg-background border border-border rounded-full flex items-center justify-center text-sm font-bold text-muted-foreground shadow-sm">2</div>
              </div>
              <div className="text-center px-4">
                <h3 className="text-xl font-semibold mb-3 text-foreground">Link Certificates</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Bulk upload PDF certificates. Match them to assets to build your immutable traceability chain.
                </p>
                <div className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600/80 dark:text-blue-400/80 bg-blue-500/5 px-2.5 py-1 rounded-md">
                  <ArrowRight className="w-3 h-3" />
                  <span>Bulk Matching</span>
                </div>
              </div>
            </div>

            {/* Step 3: GENERATE */}
            <div className="relative group">
              <div className="w-28 h-28 mx-auto bg-background rounded-2xl border border-border shadow-sm flex items-center justify-center mb-8 group-hover:border-green-500/50 group-hover:shadow-md transition-all duration-300 relative z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-muted/50 to-transparent rounded-2xl -z-10" />
                <PackageCheck className="w-10 h-10 text-green-600 dark:text-green-400" />
                <div className="absolute -top-3 -right-3 w-8 h-8 bg-background border border-border rounded-full flex items-center justify-center text-sm font-bold text-muted-foreground shadow-sm">3</div>
              </div>
              <div className="text-center px-4">
                <h3 className="text-xl font-semibold mb-3 text-foreground">Generate Pack</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  One click compiles everything into a professional, auditor-ready evidence pack with dashboard and logs.
                </p>
                <div className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600/80 dark:text-green-400/80 bg-green-500/5 px-2.5 py-1 rounded-md">
                  <ArrowRight className="w-3 h-3" />
                  <span>Instant ZIP Download</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Concierge Service Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-3 p-1 pr-4 bg-muted/30 border border-border/60 rounded-full hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-background border border-border shadow-sm">
              <Sparkles className="w-4 h-4 text-amber-500" />
            </div>
            <div className="text-sm">
              <span className="font-semibold text-foreground">Need help migrating?</span>
              <span className="mx-2 text-muted-foreground/40">|</span>
              <span className="text-muted-foreground">We offer white-glove onboarding services.</span>
            </div>
          </div>
        </div>

        {/* Video CTA */}
        {hasDemoVideo && (
          <div className="text-center">
            <button
              type="button"
              onClick={onVideoClick}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-3.5 h-3.5 fill-current" />
              </div>
              See the workflow in 60 seconds
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
